import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { formatPriceDisplay } from '../../../../util/formatting';
import { PropertyCartItem } from '../types/cart.types';
import ServiceCell from './ServiceCell';
import AccessCell from './AccessCell';
import type { FloorPlanTier } from '../floor-plan/floorPlanOffer';

type PropertyCartCardProps = {
  item: PropertyCartItem;
  onEditService?: (item: PropertyCartItem) => void;
  onEditAccess?: (item: PropertyCartItem) => void;
  onDelete?: (item: PropertyCartItem) => void;
  isMutating?: boolean;
  readOnly?: boolean;
  /** Mirrors the table's read-only Order ID column (confirmation page). */
  showOrderNumber?: boolean;
  /** DV-3204: mirrors the table's free floor plan line. */
  freeFloorPlan?: FloorPlanTier | null;
};

const FieldLabel = ({ children }: { children: string }) => (
  <Typography
    as="p"
    variant="small"
    className="mb-1 font-medium uppercase tracking-wide text-muted-foreground"
  >
    {children}
  </Typography>
);

/**
 * Below-lg card for one property cart line. Reuses the table's cell
 * components (ServiceCell, AccessCell) so table and card stay one source of
 * truth — and shows every field the table shows.
 */
const PropertyCartCard = ({
  item,
  onEditService,
  onEditAccess,
  onDelete,
  isMutating = false,
  readOnly = false,
  showOrderNumber = false,
  freeFloorPlan = null,
}: PropertyCartCardProps) => {
  const { property } = item;
  const removeLabel = item.formattedAddress
    ? `Remove ${item.formattedAddress} from cart`
    : 'Remove property from cart';

  return (
    <Card
      className={cn(
        'flex flex-col gap-3 border-border p-4',
        isMutating && 'pointer-events-none opacity-60',
      )}
      data-testid="property-cart-card"
    >
      <div className="flex items-start gap-2">
        {/* flex-col: Typography renders inline spans, which otherwise run
            together as one unbroken address line. */}
        <div className="flex min-w-0 flex-1 flex-col">
          {showOrderNumber && item.orderNumber && (
            <Typography variant="body-medium" className="text-muted-foreground">
              Order #{item.orderNumber}
            </Typography>
          )}
          <Typography variant="body-semibold">
            {property.address1 || ''}
          </Typography>
          {property.unitNumber && (
            <Typography variant="body-medium">{property.unitNumber}</Typography>
          )}
          <Typography variant="body-medium">
            {property.city || ''}, {property.state || ''} {property.zipCode || ''}
          </Typography>
        </div>
        {!readOnly &&
          onDelete &&
          (isMutating ? (
            <Spinner size="sm" />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground hover:text-primary"
              aria-label={removeLabel}
              onClick={() => onDelete(item)}
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          ))}
      </div>

      <div>
        <FieldLabel>Service</FieldLabel>
        <ServiceCell
          services={item.services}
          item={item}
          onEdit={onEditService}
          readOnly={readOnly}
          freeFloorPlan={freeFloorPlan}
        />
      </div>

      <div>
        <FieldLabel>Appointment Info</FieldLabel>
        <AccessCell
          access={item.propertyAccess}
          item={item}
          onEdit={onEditAccess}
          readOnly={readOnly}
        />
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3">
        <Typography variant="body-semibold">Total</Typography>
        <Typography variant="body-semibold">
          ${formatPriceDisplay(item.price)}
        </Typography>
      </div>
    </Card>
  );
};

export default PropertyCartCard;
